import { gql } from '@apollo/client';

import {
  ESIGN_CONTRACT_CARD_FIELDS,
  ESIGN_CONTRACT_TEMPLATE_FIELDS,
  ESIGN_MESSAGE_TEMPLATE_FIELDS,
  ESIGN_OUTREACH_FIELDS,
} from '../fragments/esign';

/**
 * ТЗ 0076 — запись по договорам на подпись.
 *
 * Каждая мутация, возвращающая `contract`, выбирает ПОЛНУЮ карточку через
 * общий фрагмент: ответ сам обновляет запись кэша, из которой читает ящик, и
 * отдельный перезапрос после действия не нужен.
 *
 * Имена операций отличаются от полей бэкенда (скилл graphql-codegen).
 */
export const CreateStudentContractMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation CreateStudentContract($input: StudentContractDraftInput!) {
    createStudentContractDraft(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const UpdateStudentContractMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation UpdateStudentContract($id: ID!, $input: StudentContractDraftInput!) {
    updateContractDraft(id: $id, input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

/**
 * Договор с фирмой-клиентом (B2B). Подписывает представитель фирмы;
 * участники — только список в Zamówieniu, их не зачисляют автоматически.
 */
export const CreateCompanyContractMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation CreateCompanyContract($input: CompanyContractDraftInput!) {
    createCompanyContractDraft(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const UpdateCompanyContractMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation UpdateCompanyContract($id: ID!, $input: CompanyContractDraftInput!) {
    updateCompanyContractDraft(id: $id, input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const SendEsignContractMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation SendEsignContract(
    $id: ID!
    $sendEmail: Boolean!
    $emailSubject: String
    $emailBody: String
    $templateId: ID
  ) {
    sendContract(
      id: $id
      sendEmail: $sendEmail
      emailSubject: $emailSubject
      emailBody: $emailBody
      templateId: $templateId
    ) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const ResendEsignEmailMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation ResendEsignEmail(
    $id: ID!
    $emailSubject: String
    $emailBody: String
    $templateId: ID
  ) {
    resendContractEmail(
      id: $id
      emailSubject: $emailSubject
      emailBody: $emailBody
      templateId: $templateId
    ) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

/**
 * 🔴 Ссылка на подпись — bearer-секрет. Хук зовёт эту мутацию с
 * `fetchPolicy: 'no-cache'`, чтобы `url` не осел в кэше Apollo (ответ мутации
 * иначе нормализуется в ROOT_MUTATION), кладёт его в буфер обмена и нигде не
 * рисует и не логирует.
 */
export const RevealEsignLinkMutation = gql`
  mutation RevealEsignLink($id: ID!) {
    revealContractSigningLink(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
      url
      expiresAt
    }
  }
`;

export const CancelEsignContractMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation CancelEsignContract($id: ID!, $reason: String!) {
    cancelContract(id: $id, reason: $reason) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const RetryEsignFulfilmentMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation RetryEsignFulfilment($id: ID!, $existingUserId: ID) {
    retryContractFulfilment(id: $id, existingUserId: $existingUserId) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const CreateTeacherFrameworkMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation CreateTeacherFramework(
    $teacherId: ID!
    $templateCode: String
    $templateId: ID
    $courseIds: [ID!]
  ) {
    createTeacherFrameworkDraft(
      teacherId: $teacherId
      templateCode: $templateCode
      templateId: $templateId
      courseIds: $courseIds
    ) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

export const CreateTeacherAnnexMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation CreateTeacherAnnex($applicationId: ID!, $templateId: ID) {
    createTeacherAnnexDraft(
      applicationId: $applicationId
      templateId: $templateId
    ) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

/**
 * ТЗ 0077 — ручное управление SMS-напоминаниями договора студента.
 *
 * Каждая мутация возвращает полное состояние напоминаний тем же фрагментом,
 * что и запрос ящика: хук пишет его в кэш запроса, перезапрос не нужен.
 * Имена операций отличаются от полей бэкенда (скилл graphql-codegen).
 */
export const RequestContractReminderMutation = gql`
  ${ESIGN_OUTREACH_FIELDS}
  mutation RequestContractReminder($contractId: ID!) {
    sendContractReminderNow(contractId: $contractId) {
      success
      customErrors {
        field
        code
        message
      }
      outreach {
        ...EsignOutreachFields
      }
    }
  }
`;

export const PauseContractRemindersMutation = gql`
  ${ESIGN_OUTREACH_FIELDS}
  mutation PauseContractReminders($contractId: ID!) {
    stopContractReminders(contractId: $contractId) {
      success
      customErrors {
        field
        code
        message
      }
      outreach {
        ...EsignOutreachFields
      }
    }
  }
`;

export const ContinueContractRemindersMutation = gql`
  ${ESIGN_OUTREACH_FIELDS}
  mutation ContinueContractReminders($contractId: ID!) {
    resumeContractReminders(contractId: $contractId) {
      success
      customErrors {
        field
        code
        message
      }
      outreach {
        ...EsignOutreachFields
      }
    }
  }
`;

/**
 * ТЗ 0078 — сменить продавца договора. Только Admin и только с причиной;
 * смена пишется в журнал. Возвращает полную карточку тем же фрагментом, что
 * и остальные мутации: ящик обновится из ответа.
 */
export const ReassignEsignSellerMutation = gql`
  ${ESIGN_CONTRACT_CARD_FIELDS}
  mutation ReassignEsignSeller($id: ID!, $sellerId: ID!, $reason: String!) {
    reassignContractSeller(id: $id, sellerId: $sellerId, reason: $reason) {
      success
      customErrors {
        field
        code
        message
      }
      contract {
        ...EsignContractCardFields
      }
    }
  }
`;

/**
 * 🔴 Короткая ссылка ведёт на подпись — такой же bearer-секрет, как и
 * длинная. Хук зовёт мутацию с `fetchPolicy: 'no-cache'` и отдаёт `url`
 * прямо в буфер обмена (приём `RevealEsignLink`).
 */
export const RevealEsignShortLinkMutation = gql`
  mutation RevealEsignShortLink($contractId: ID!) {
    revealContractShortLink(contractId: $contractId) {
      success
      customErrors {
        field
        code
        message
      }
      url
      expiresAt
    }
  }
`;

/**
 * Новое письмо подписанту из ящика продавца или общего (вкладка «Переписка»).
 * Ответ подписанта придёт в тот же ящик и появится после синхронизации Gmail.
 *
 * ТЗ 0078: тема и текст могут содержать подстановки — их заполняет СЕРВЕР
 * (`{link}` — короткая ссылка, пока договор ждёт подписи). `templateId` —
 * какой шаблон взят за основу, для журнала.
 */
export const WriteContractEmailMutation = gql`
  mutation WriteContractEmail(
    $contractId: ID!
    $mailboxEmail: String!
    $subject: String!
    $text: String
    $html: String
    $attachmentIds: [ID]
    $templateId: ID
  ) {
    sendContractEmail(
      contractId: $contractId
      mailboxEmail: $mailboxEmail
      subject: $subject
      text: $text
      html: $html
      attachmentIds: $attachmentIds
      templateId: $templateId
    ) {
      success
      customErrors {
        field
        code
        message
      }
      conversationId
    }
  }
`;

/**
 * ТЗ 0078 — SMS подписанту со своим текстом (обычно из шаблона). Защиты
 * доставки те же, что у напоминаний; кулдаун общий с «Напомнить сейчас».
 * Возвращает состояние напоминаний тем же фрагментом, что и запрос секции.
 */
export const SendContractSmsTextMutation = gql`
  ${ESIGN_OUTREACH_FIELDS}
  mutation SendContractSmsText(
    $contractId: ID!
    $text: String!
    $templateId: ID
  ) {
    sendContractSms(
      contractId: $contractId
      text: $text
      templateId: $templateId
    ) {
      success
      customErrors {
        field
        code
        message
      }
      outreach {
        ...EsignOutreachFields
      }
    }
  }
`;

/**
 * ТЗ 0083 — одна SMS многим подписантам из списка договоров. Уходит только
 * отправленным и не подписанным договорам, чью переписку ведёт сотрудник;
 * остальные — строкой с причиной. `requestKey` — один на нажатие
 * «Отправить»: повтор запроса (сеть, двойной клик) второй раз не шлёт.
 */
export const SendContractsSmsBulkMutation = gql`
  mutation SendContractsSmsBulk(
    $contractIds: [ID!]!
    $text: String!
    $templateId: ID
    $requestKey: String!
  ) {
    sendContractsSms(
      contractIds: $contractIds
      text: $text
      templateId: $templateId
      requestKey: $requestKey
    ) {
      success
      sent
      skipped
      customErrors {
        field
        code
        message
      }
      rows {
        contractId
        number
        signerName
        ok
        code
        message
        segments
      }
    }
  }
`;

/** Справочник шаблонов SMS и писем: создать или изменить — только Admin. */
export const SaveContractMessageTemplateMutation = gql`
  ${ESIGN_MESSAGE_TEMPLATE_FIELDS}
  mutation SaveContractMessageTemplate(
    $id: ID
    $channel: String!
    $name: String!
    $namePl: String
    $subject: String
    $body: String!
    $isActive: Boolean
    $topic: String
    $personal: Boolean
  ) {
    saveMessageTemplate(
      id: $id
      channel: $channel
      name: $name
      namePl: $namePl
      subject: $subject
      body: $body
      isActive: $isActive
      topic: $topic
      personal: $personal
    ) {
      success
      customErrors {
        field
        code
        message
      }
      template {
        ...EsignMessageTemplateFields
      }
    }
  }
`;

/** Удалить свой шаблон — только Admin; системный можно лишь выключить. */
export const DeleteContractMessageTemplateMutation = gql`
  mutation DeleteContractMessageTemplate($id: ID!) {
    deleteMessageTemplate(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
      deletedId
    }
  }
`;

/**
 * ТЗ 0107 — редактор шаблонов договоров (только Admin). Имена операций
 * отличаются от полей бэкенда. `problems` — ошибки проверки текста с номером
 * чипа (подсветка в редакторе); `rerenderedDrafts` — сколько неотправленных
 * черновиков сервер перерисовал новым текстом.
 */
export const StoreContractTemplateVersionMutation = gql`
  ${ESIGN_CONTRACT_TEMPLATE_FIELDS}
  mutation StoreContractTemplateVersion(
    $id: ID!
    $html: String!
    $tokens: JSONString
    $comment: String
  ) {
    saveContractTemplateVersion(
      id: $id
      html: $html
      tokens: $tokens
      comment: $comment
    ) {
      success
      customErrors {
        field
        code
        message
      }
      problems {
        code
        message
        token
      }
      rerenderedDrafts
      template {
        ...EsignContractTemplateFields
      }
    }
  }
`;

export const CopyContractTemplateMutation = gql`
  ${ESIGN_CONTRACT_TEMPLATE_FIELDS}
  mutation CopyContractTemplate(
    $basedOnId: ID!
    $name: String!
    $code: String
    $legalEntityId: ID
    $appendix: String
  ) {
    createContractTemplate(
      basedOnId: $basedOnId
      name: $name
      code: $code
      legalEntityId: $legalEntityId
      appendix: $appendix
    ) {
      success
      customErrors {
        field
        code
        message
      }
      problems {
        code
        message
        token
      }
      template {
        ...EsignContractTemplateFields
      }
    }
  }
`;

export const SwitchContractTemplateVersionMutation = gql`
  ${ESIGN_CONTRACT_TEMPLATE_FIELDS}
  mutation SwitchContractTemplateVersion($id: ID!) {
    activateContractTemplateVersion(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
      problems {
        code
        message
        token
      }
      rerenderedDrafts
      template {
        ...EsignContractTemplateFields
      }
    }
  }
`;

export const ToggleContractTemplateCodeMutation = gql`
  ${ESIGN_CONTRACT_TEMPLATE_FIELDS}
  mutation ToggleContractTemplateCode($code: String!, $active: Boolean!) {
    setContractTemplateActive(code: $code, active: $active) {
      success
      customErrors {
        field
        code
        message
      }
      problems {
        code
        message
        token
      }
      template {
        ...EsignContractTemplateFields
      }
    }
  }
`;
